/**
 * 渐变**写入**：应用 / 调整 / 取消 —— 一律产出可撤销的 Command。
 *
 * 三条纪律：
 *  1) 构造期**零副作用**：新节点是 detached 的（还没挂进文档），挂载与摘除都放在补丁里；
 *  2) 结构改动用 `makeStructuralPatch`（apply 走真实 DOM 操作、revert 退快照）；
 *  3) **id 稳定**：切换 线性↔径向 是换节点但**沿用同一个 id** —— 页面上的 `url(#id)` 一处都不用改。
 *
 * 新建渐变用 `gradientUnits="objectBoundingBox"`（自动跟随元素尺寸，不用算 bbox）；
 * 编辑作者已有的渐变时不动它的 `gradientUnits`（见 updateGradient 的说明）。
 */
import { SVG_NS, pathOf } from '../dom';
import type { Command } from '../history';
import { attrPatch, makeCustomPatch, makeStructuralPatch, setInnerXml, snapshotOf, type Patch } from '../patch';
import { setFill, setStroke } from './style';
import { readGradientById, type GradientStop, type GradientType } from '../query/gradient';

/** 渐变挂在哪条属性上 */
export type PaintWhich = 'fill' | 'stroke';

export interface GradientOpsOpts {
  /** 默认 fill（`which: 'stroke'` 就是描边渐变，共用同一套 def） */
  which?: PaintWhich;
  mergeKey?: string;
}

export interface GradientSpec {
  type: GradientType;
  stops: GradientStop[];
  /** 线性方向角（度）：0 = 左→右，90 = 上→下 */
  angle?: number;
  /** 线性向量长度（bbox 单位）；手写 0,0→1,1 这种比 1 长，调整角度时按原长度保留 */
  length?: number;
  /** 径向中心与半径（bbox 单位） */
  radial?: { cx: number; cy: number; r: number };
}

const fmt = (n: number): string => String(Number(n.toFixed(4)));

const tagOf = (type: GradientType): string => (type === 'linear' ? 'linearGradient' : 'radialGradient');

/** 角度 + 长度 → bbox 空间的 x1/y1/x2/y2（与 readGradient 的反算严格互逆） */
const linearAttrs = (angle: number, length: number): Record<string, string> => {
  const rad = (angle * Math.PI) / 180;
  const hx = (Math.cos(rad) * length) / 2;
  const hy = (Math.sin(rad) * length) / 2;
  return { x1: fmt(0.5 - hx), y1: fmt(0.5 - hy), x2: fmt(0.5 + hx), y2: fmt(0.5 + hy) };
};

/** 下一个可用 id：沿用 `ext-N` 的"取最大 + 1"策略，永不重用 */
export function nextGradientId(root: Element): string {
  let max = 0;
  const walkIds = (parent: Element): void => {
    for (const el of Array.from(parent.children)) {
      const m = /^ext-grad-(\d+)$/.exec(el.getAttribute('id') ?? '');
      if (m) max = Math.max(max, Number(m[1]));
      walkIds(el);
    }
  };
  walkIds(root);
  return `ext-grad-${max + 1}`;
}

/** 建一个 detached 的渐变节点（含色标）—— 此时还没进文档 */
function buildGradientNode(root: Element, id: string, spec: GradientSpec): Element {
  const doc = root.ownerDocument as Document;
  const node = doc.createElementNS(SVG_NS, tagOf(spec.type));
  node.setAttribute('id', id);
  node.setAttribute('gradientUnits', 'objectBoundingBox');
  if (spec.type === 'linear') {
    for (const [k, v] of Object.entries(linearAttrs(spec.angle ?? 90, spec.length ?? 1))) node.setAttribute(k, v);
  } else {
    const rad = spec.radial ?? { cx: 0.5, cy: 0.5, r: 0.5 };
    node.setAttribute('cx', fmt(rad.cx));
    node.setAttribute('cy', fmt(rad.cy));
    node.setAttribute('r', fmt(rad.r));
  }
  for (const s of spec.stops) {
    const stop = doc.createElementNS(SVG_NS, 'stop');
    stop.setAttribute('offset', fmt(s.offset));
    stop.setAttribute('stop-color', s.color);
    if (s.opacity < 1) stop.setAttribute('stop-opacity', fmt(s.opacity));
    node.appendChild(stop);
  }
  return node;
}

/** 色标 → 内部 XML（复用容器的快照机制，命名空间与自闭合写法都一致） */
const stopsXml = (root: Element, spec: GradientSpec): string =>
  snapshotOf(buildGradientNode(root, 'tmp-stops', { ...spec, stops: spec.stops }));

/** 语义比较（不比字符串：作者可能写 `0%`、或属性顺序不同） */
function sameStops(a: GradientStop[], b: GradientStop[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((s, i) => {
    const t = b[i] as GradientStop;
    return (
      s.color.toLowerCase() === t.color.toLowerCase() &&
      Math.abs(s.offset - t.offset) < 1e-4 &&
      Math.abs(s.opacity - t.opacity) < 1e-4
    );
  });
}

function findDefs(root: Element): Element | null {
  for (const el of Array.from(root.children)) {
    if (el.tagName.toLowerCase() === 'defs') return el;
  }
  return null;
}

/**
 * 给目标元素应用渐变（新建 def 节点 + 写 `fill="url(#id)"`）。
 * 多个元素共用一个节点（像 Illustrator），不是每个元素复制一份。
 */
export function applyGradient(
  root: Element,
  targets: Element[],
  spec: GradientSpec,
  opts: GradientOpsOpts = {},
): Command {
  if (targets.length === 0) return { label: '应用渐变', patches: [] };
  const id = nextGradientId(root);
  const node = buildGradientNode(root, id, spec);
  const existing = findDefs(root);
  const defs = existing ?? (root.ownerDocument as Document).createElementNS(SVG_NS, 'defs');
  const defsIsNew = existing === null;

  // 用闭包 patch 而不是"整根快照"：后者会在撤销时重建 root 的所有子节点，
  // 让选中集与图层列表持有的元素引用全部变成游离节点（patch.ts 里踩过这个坑）。
  const structural = makeCustomPatch(
    '应用渐变',
    () => {
      if (defs.parentNode !== root) root.appendChild(defs); // 新建的 defs：挂进文档
      defs.appendChild(node);
    },
    () => {
      node.remove();
      if (defsIsNew) defs.remove();
    },
    64,
  );

  const paint = opts.which === 'stroke' ? setStroke : setFill;
  const patches: Patch[] = [structural, ...paint(root, targets, `url(#${id})`).patches];
  return { label: `应用${opts.which === 'stroke' ? '描边' : ''}渐变（${spec.stops.length} 色标）`, patches, mergeKey: opts.mergeKey };
}

/**
 * 调整已有渐变：改方向/中心，或整体替换色标。
 * 类型切换（线性↔径向）要换标签，做法是**换节点但保留 id** —— `url(#id)` 引用不用动。
 * 注意：不动 `gradientUnits`（作者写的是 userSpaceOnUse 就继续用 userSpaceOnUse），
 * 但此时写回的坐标是 bbox 空间的数 —— 所以只有 bbox 单位才调整方向（见 guard）。
 */
export function updateGradient(
  root: Element,
  id: string,
  spec: GradientSpec,
  opts: GradientOpsOpts = {},
): Command {
  const mergeKey = opts.mergeKey;
  const info = readGradientById(root, id);
  if (!info) return { label: '调整渐变', patches: [] };
  const { node } = info;
  const patches: Patch[] = [];

  if (node.tagName.toLowerCase() !== tagOf(spec.type).toLowerCase()) {
    const parent = node.parentNode as Element | null;
    if (!parent) return { label: '切换渐变类型', patches: [] };
    const next = buildGradientNode(root, id, spec);
    patches.push(
      makeStructuralPatch(parent, snapshotOf(parent), '切换渐变类型', () => {
        parent.replaceChild(next, node);
      }),
    );
    return { label: '切换渐变类型', patches, mergeKey };
  }

  const path = pathOf(node, root);
  if (!path) return { label: '调整渐变', patches: [] };

  if (info.units === 'objectBoundingBox') {
    const attrs =
      spec.type === 'linear'
        ? linearAttrs(spec.angle ?? info.angle, spec.length ?? info.length)
        : ((): Record<string, string> => {
            const r = spec.radial ?? info.radial;
            return { cx: fmt(r.cx), cy: fmt(r.cy), r: fmt(r.r) };
          })();
    for (const [name, value] of Object.entries(attrs)) {
      const p = attrPatch(path, name, node.getAttribute(name), value);
      if (p) patches.push(p);
    }
  }

  if (!sameStops(info.stops, spec.stops)) {
    const xml = stopsXml(root, spec);
    patches.push(makeStructuralPatch(node, snapshotOf(node), '调整色标', () => setInnerXml(node, xml)));
  }

  return { label: '调整渐变', patches, mergeKey };
}

/**
 * 取消渐变：目标元素回到纯色（取第一个色标色）；**没人再引用**时把节点也删掉。
 * （被别的元素引用时留着 —— 那些元素还在用它。）
 */
export function removeGradient(
  root: Element,
  targets: Element[],
  id: string,
  opts: GradientOpsOpts = {},
): Command {
  const info = readGradientById(root, id);
  const fallback = info?.stops[0]?.color ?? '#000000';
  const which: PaintWhich = opts.which ?? 'fill';
  const paint = which === 'stroke' ? setStroke : setFill;
  const patches: Patch[] = [...paint(root, targets, fallback).patches];

  const dropping = targets.filter((el) => (el.getAttribute(which) ?? '').includes(`url(#${id})`)).length;
  if (info && info.used - dropping <= 0) {
    const parent = info.node.parentNode as Element | null;
    if (parent) {
      patches.push(
        makeStructuralPatch(parent, snapshotOf(parent), '删除渐变节点', () => info.node.remove()),
      );
    }
  }

  return { label: `取消${which === 'stroke' ? '描边' : ''}渐变`, patches };
}
